import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import {
  Divider,
  Icon,
  Label,
  Placeholder,
  Popup,
  type SemanticCOLORS,
  type SemanticICONS,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { LabelItem } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas";
import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips";
import { Checkbox } from "components/lib/UI";

import { useImpactStageMap } from "../Ideas/ImpactStatements/StageSegments";
import {
  formatImpact,
  impactMultipliers,
} from "../Ideas/ImpactStatements/statementUtils";
import type { IIdeaCard } from "./Board";

const StyledCard = styled.div<{
  $highlightCard?: boolean;
  $isAssigned?: boolean;
  $isSelecting?: boolean;
  $isSelected?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;

  box-sizing: border-box;
  overflow: visible !important;

  box-shadow:
    rgba(9, 30, 66, 0.25) 0px 1px 1px 0px,
    rgba(9, 30, 66, 0.31) 0px 0px 1px 0px;
  border-radius: 5px;
  border: ${({ $highlightCard }) => ($highlightCard ? "2px solid red" : null)};
  padding: 8px 14px;

  background: white;
  ${({ $isSelecting }) =>
    !$isSelecting ?
      `
    &:hover {
      background-color: #f4f5f7;
    }
  `
    : `
    cursor: default;
  `}

  transition: transform 0.08s ease-in-out;

  ${({ $isSelecting, $isSelected }) =>
    $isSelecting ?
      `
    &:hover {
      .icon-checkbox {
        font-size: 2em !important;
      }
    }
    &:not(:hover) {
      ${
        !$isSelected &&
        `
        >*:not(.selection-box) {
          opacity: 0.6;
        }
      `
      }
    }
    >*:not(.selection-box) {
      cursor: pointer;
    }
  `
    : null}
  .selection-box {
    opacity: 1;
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 10;
  }
  header {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const CardDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  .labels {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  .labels .label-chip {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .more-labels {
    font-size: 12px;
    line-height: 10px;
    display: block;
    margin-left: 6px;
  }
`;

const MetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 0.5px solid rgba(0, 0, 0, 0.2);
  padding-top: 5px;
  gap: 0;
`;

const AssigneeImage = styled.div<{
  $isCurrentUser?: boolean;
  $user?: OpenAPI.Schemas["User"];
  $isOtherChip?: boolean;
}>`
  display: inline-block;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  margin-right: -7px;
  box-shadow: 3px 3px 5px 0px rgba(0, 0, 0, 0.1);
  ${({ $isCurrentUser, theme }) =>
    $isCurrentUser ?
      `
    border: 3px solid ${theme.accent};
    width: 38px;
    height: 38px;
  `
    : ""}

  ${({ $user, $isOtherChip }) =>
    (
      !$isOtherChip &&
      ($user?.profile?.avatar ||
        !$user?.profile?.firstName ||
        !$user?.profile?.lastName)
    ) ?
      `
    background-size: cover;
    background-position: center center;
    background-image: url(${util.mixinCssUrlFallback(util.avatarUrl($user), util.avatarUrl())});
  `
    : `
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #F3F3F3;
    > span {
      display: block;
      color: #666666;
      font-weight: bold;
      text-align: center;
    }
  `}
`;

const StyledStatusLabel = styled.div<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  > div {
    width: 8px;
    height: 8px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: 8px;
  }
  > span {
    font-size: 11px;
  }
`;

const AssigneeImageContainer = styled.div<{ $count?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-right: ${({ $count = 0 }) => 7 + Math.min($count, 6) * 3}px;

  ${AssigneeImage} {
    margin-right: -${({ $count = 0 }) => 7 + Math.min($count, 6) * 3}px;
  }
`;

const getDeadlineStatus: (
  deadline: string | Date,
) =>
  | {
      color: SemanticCOLORS;
      icon: SemanticICONS;
      text: string;
      isOverdue?: boolean;
    }
  | undefined = (deadline) => {
  if (typeof deadline === "string") {
    deadline = new Date(deadline);
  }
  if (!deadline) return;
  if (deadline > moment().add(3, "days").toDate())
    return {
      color: "green" as const,
      icon: "calendar" as const,
      text: "On track to deadline",
    };
  if (deadline < new Date()) {
    return {
      color: "red" as const,
      icon: "calendar" as const,
      text: "Deadline overdue",
      isOverdue: true,
    };
  }
  if (deadline > new Date())
    return {
      color: "orange" as const,
      icon: "calendar" as const,
      text: "Approaching deadline",
    };
};

const getReviewDateStatus: (
  reviewDate: string | Date,
) =>
  | {
      color: SemanticCOLORS;
      icon: SemanticICONS;
      text: string;
      isOverdue?: boolean;
    }
  | undefined = (reviewDate) => {
  if (typeof reviewDate === "string") {
    reviewDate = new Date(reviewDate);
  }
  if (!reviewDate) return;
  if (reviewDate > moment().add(3, "days").toDate())
    return {
      color: "green" as const,
      icon: "calendar" as const,
      text: "On track to review date",
    };
  if (reviewDate < new Date()) {
    return {
      color: "red" as const,
      icon: "calendar" as const,
      text: "Review date overdue",
      isOverdue: true,
    };
  }
  if (reviewDate > new Date())
    return {
      color: "orange" as const,
      icon: "calendar" as const,
      text: "Approaching review date",
    };
};

const getWarning = (board) => {
  const updatedAt = board?.updatedAt && moment(board.updatedAt);
  if (!updatedAt) {
    return { updatedAt: undefined, status: "green" };
  }

  const lowerLimit = moment().subtract(1, "months");
  const upperLimit = moment().subtract(3, "months");
  let status = "green";
  if (updatedAt.isAfter(lowerLimit)) {
    status = "green";
  } else if (updatedAt.isBefore(lowerLimit) && updatedAt.isAfter(upperLimit)) {
    status = "orange";
  } else {
    status = "red";
  }
  return { updatedAt, status };
};

type ICustomCard = {
  card: IIdeaCard;
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  user: OpenAPI.GET<"/users/me">["response"];
  highlightCard?: string;
  onClick: (ideaId: string) => void;
  isSelecting?: boolean;
  isSelected?: boolean;
  onSelect: (idea: OpenAPI.Schemas["Idea"]) => void;
  loading?: boolean;
};

const annualImpactValue = (usesImpactStatementSystem, impact) => {
  let value = "";
  if (usesImpactStatementSystem) {
    value = formatImpact(impact.ownerImpact, impact, { annually: true });
  } else if (typeof impact?.value === "string") {
    value = impact?.value;
  } else {
    const annual = (impactMultipliers[impact?.frequency] ?? 1) * impact?.value;
    if (impact?.ownerImpact?.type === "money") {
      value = `£${annual}`;
    } else {
      value = `${annual}`;
    }
  }

  return value;
};

const generateImpactText = (t, usesImpactStatementSystem, impact) => {
  const effectMap = { cost: "cost", saving: "save" };

  const effectText =
    effectMap[impact?.ownerImpact?.effect] ??
    `have ${t("generic.impactWithArticle")} of`;
  let text = `This ${t("generic.idea")} will ${effectText} `;
  if (usesImpactStatementSystem) {
    text += formatImpact(impact.ownerImpact, impact);
  } else {
    if (impact?.ownerImpact?.type === "time") {
      text += `${impact?.value} hours`;
    } else if (impact?.ownerImpact?.type === "money") {
      text += `£${impact?.value}`;
    } else {
      text += `"${impact?.value}"`;
    }
  }

  if (!impact?.frequency || impact?.frequency === "one-off") {
    text += " once";
  } else {
    text += ` ${impact?.frequency}`;
    if (impact?.frequency !== "annually") {
      text += ` (${annualImpactValue(usesImpactStatementSystem, impact)} annually)`;
    }
  }
  text += ".";

  return (
    <>
      <p>
        <strong>{impact?.ownerImpact?.name}</strong>
        {impact?.ownerImpactCategory ?
          <span>
            <br />
            in {impact?.ownerImpactCategory?.name}
          </span>
        : null}
      </p>
      <p>{text}</p>
      {impact.choice?.weight !== undefined ?
        <div
          style={{ display: "flex", alignItems: "center", lineHeight: "1em" }}
        >
          <Icon
            name="level up alternate"
            size="small"
            style={{ marginRight: 2 }}
          />
          <div>
            Weight: {impact.choice?.weight || 0}/
            {impact.ownerImpact?.maxWeight || 0}
          </div>
        </div>
      : null}
      {impact?.justification ?
        <>
          <Divider />
          <b>Justification</b>
          <p>{impact?.justification}</p>
        </>
      : null}
    </>
  );
};

const CustomCard = (props: ICustomCard) => {
  const { idea, laneMetaType } = props.card;
  const {
    user,
    challenge,
    onClick,
    highlightCard,
    isSelecting,
    onSelect,
    isSelected,
    loading,
  } = props;
  const { t } = useTranslation();
  const stageMap = useImpactStageMap();
  const theme = useTheme();

  const [showAllImpacts, setShowAllImpacts] = useState(false);

  const cardProps = useMemo(
    () =>
      idea?._id ?
        isSelecting ?
          {
            $isSelecting: true,
            $isSelected: isSelected,
            onClick: () => onSelect(idea),
          }
        : {
            onClick: () => onClick(idea._id),
            $highlightCard: highlightCard === idea._id,
          }
      : null,
    [highlightCard, idea, isSelected, isSelecting, onClick, onSelect],
  );

  const usesImpactStatementSystem =
    user?.ownerOrganisation?.enabledFeatures?.includes("impactStatements");

  const queryParams = useIdeaQueryParams("board");

  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [idea.challenge, queryParams], {
      select: (data) => data.ideaImpacts?.[idea._id],
    }),
  );

  if (!idea || !idea?._id) return null;
  const board = idea?.projectManagement?.boards?.find(
    (b) => b.forId === idea.challenge,
  );
  const deadlineStatus = getDeadlineStatus(board?.deadline);
  const reviewDateStatus = getReviewDateStatus(board?.reviewDate);
  const { updatedAt, status } = getWarning(board);
  const submittedAssessments = idea.assessments?.filter(
    (a) => a.isSubmitted,
  ).length;
  const cardLaneHasMetaType =
    laneMetaType &&
    (laneMetaType === "notProgressing" ||
      laneMetaType === "completed" ||
      laneMetaType === "resolved");

  let submittedColor = "green";
  if (submittedAssessments === 0) {
    submittedColor = "red";
  } else if (submittedAssessments < idea.assessments?.length) {
    submittedColor = "orange";
  }

  const allImpacts = ideaImpactsQuery?.data?.totalImpacts || [];
  const pinnedImpacts = allImpacts?.filter((i) => i.isPinned);
  const hiddenImpactsCount =
    (allImpacts?.length || 0) - (pinnedImpacts?.length || 0);
  const impactsToRender =
    showAllImpacts || !usesImpactStatementSystem ? allImpacts : pinnedImpacts;

  const hasUserEstimate =
    ideaImpactsQuery?.data?.completedImpactStages?.userEstimate;
  const hasProjectEstimate =
    ideaImpactsQuery?.data?.completedImpactStages?.projectEstimate;
  const hasFinalOutcome =
    ideaImpactsQuery?.data?.completedImpactStages?.finalOutcome;

  const currentStage =
    hasFinalOutcome ? "finalOutcome"
    : hasProjectEstimate ? "projectEstimate"
    : hasUserEstimate ? "userEstimate"
    : null;

  return (
    <StyledCard {...cardProps}>
      {isSelecting ?
        <Checkbox
          className="selection-box"
          isSelected={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(idea);
          }}
        />
      : null}
      <header>
        <CardHeader>
          <Popup
            trigger={<h4 style={{ margin: 0 }}>{idea.name}</h4>}
            content={idea.name}
            disabled={!!idea.name && idea.name.length <= 70}
          />
          <div>
            {idea.children ?
              <Popup
                trigger={<Icon name="sitemap" />}
                content={`This ${t("generic.idea")} is a merged ${t("generic.idea")}.`}
              />
            : null}
          </div>
        </CardHeader>
      </header>
      <CardDetails onDragStart={(e) => e.preventDefault()}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {idea.labels?.length ?
            <div className="labels">
              {idea.labels.slice(0, 5).map((label) => (
                <div key={label} className="label-chip">
                  <LabelItem
                    label={label}
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  />
                </div>
              ))}
              {idea.labels?.length > 5 ?
                <Popup
                  trigger={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <LabelItem label={`+ ${idea.labels?.length - 5} more`} />
                    </div>
                  }
                  content={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      {idea.labels.slice(5).map((label) => (
                        <div key={label}>
                          <LabelItem label={label} />
                        </div>
                      ))}
                    </div>
                  }
                />
              : null}
            </div>
          : null}
          {!idea.isSubmitted ?
            <StyledStatusLabel $backgroundColor="orange">
              <div />
              <span>Draft {t("generic.idea")}</span>
            </StyledStatusLabel>
          : null}
          {updatedAt ?
            <Popup
              content={`This ${t("generic.idea")} was last updated on ${updatedAt.format("DD/MM/YYYY")} (${updatedAt.fromNow()})`}
              position="right center"
              trigger={
                <StyledStatusLabel $backgroundColor={status}>
                  <div />
                  <span>Updated: {moment(updatedAt).format("Do MMM 'YY")}</span>
                </StyledStatusLabel>
              }
            />
          : null}
          {deadlineStatus && !cardLaneHasMetaType ?
            <Popup
              content={`${deadlineStatus.text} : ${moment(board.deadline).format("DD/MM/YYYY")} (${moment(board.deadline).fromNow()})`}
              position="right center"
              trigger={
                <StyledStatusLabel $backgroundColor={deadlineStatus.color}>
                  <div />
                  <span>
                    Deadline: {moment(board.deadline).format("Do MMM 'YY")}
                  </span>
                </StyledStatusLabel>
              }
            />
          : null}
          {reviewDateStatus && !cardLaneHasMetaType ?
            <Popup
              content={`${reviewDateStatus.text} : ${moment(board.reviewDate).format("DD/MM/YYYY")} (${moment(board.reviewDate).fromNow()})`}
              position="right center"
              trigger={
                <StyledStatusLabel $backgroundColor={reviewDateStatus.color}>
                  <div />
                  <span>
                    Review date: {moment(board.reviewDate).format("Do MMM 'YY")}
                  </span>
                </StyledStatusLabel>
              }
            />
          : null}
          {idea.assessments?.length ?
            <Popup
              content={`${submittedAssessments} of a total ${idea?.assessmentDetails?.totalCount || 0} assessments have been submitted.`}
              position="right center"
              trigger={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    backgroundColor: "#6435c9",
                    marginLeft: -4.5,
                    padding: "2px 5px",
                    color: "#fff",
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: submittedColor,
                      borderRadius: 8,
                    }}
                  />
                  <span style={{ fontSize: 11 }}>
                    {loading ?
                      "Loading assessments..."
                    : `Assessed: ${idea?.assessmentDetails?.submittedCount || 0}/${idea?.assessmentDetails?.totalCount || 0}`
                    }
                  </span>
                </div>
              }
            />
          : null}
        </div>
        <AssigneeImageContainer $count={board?.assigneeUsers?.length}>
          {board?.assigneeUsers?.length > 0 ?
            <>
              {board.assigneeUsers.slice(0, 6).map((assignee) => (
                <Popup
                  key={assignee._id}
                  content={
                    assignee._id === user._id ?
                      `You are assigned to this ${t("generic.idea")}.`
                    : `${assignee.profile.fullName} is assigned to this ${t("generic.idea")}.`
                  }
                  trigger={
                    <AssigneeImage
                      key={assignee._id}
                      $user={assignee}
                      $isCurrentUser={assignee._id === user._id}
                    >
                      {!assignee?.profile?.avatar &&
                        assignee.profile.firstName &&
                        assignee.profile.lastName && (
                          <span>
                            {assignee.profile.firstName[0].toUpperCase()}
                            {assignee.profile.lastName[0].toUpperCase()}
                          </span>
                        )}
                    </AssigneeImage>
                  }
                />
              ))}
              {board.assigneeUsers?.length > 6 ?
                <Popup
                  content={board.assigneeUsers.slice(6).map((a) => (
                    <p key={a._id}>{a.profile.fullName}</p>
                  ))}
                  trigger={
                    <AssigneeImage $isOtherChip={true}>
                      <span>{`+${board.assigneeUsers?.length - 6}`}</span>
                    </AssigneeImage>
                  }
                />
              : null}
            </>
          : null}
        </AssigneeImageContainer>
      </CardDetails>
      {allImpacts.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #eee",
            padding: 5,
            borderRadius: 4,
            gap: 6,
          }}
        >
          {usesImpactStatementSystem ?
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Popup
                content={
                  <div>
                    <p style={{ fontWeight: "bold" }}>
                      {stageMap[currentStage]?.name}
                    </p>
                    {stageMap[currentStage]?.description}
                  </div>
                }
                trigger={
                  <div style={{ display: "flex", gap: 2, marginBottom: 4 }}>
                    {challenge?.impactStatements?.showOnIdea ?
                      <>
                        <Icon
                          name={stageMap.userEstimate.icon}
                          style={{
                            opacity:
                              (
                                hasUserEstimate ||
                                hasProjectEstimate ||
                                hasFinalOutcome
                              ) ?
                                1
                              : 0.3,
                          }}
                        />
                        <Icon
                          name="arrow right"
                          style={{
                            opacity:
                              hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                          }}
                        />
                      </>
                    : null}
                    <Icon
                      name={stageMap.projectEstimate.icon}
                      style={{
                        opacity:
                          hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                      }}
                    />
                    <Icon
                      name="arrow right"
                      style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
                    />
                    <Icon
                      name={stageMap.finalOutcome.icon}
                      style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
                    />
                  </div>
                }
                position="top left"
                size="small"
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0px",
                }}
              >
                {!showAllImpacts && hiddenImpactsCount > 0 ?
                  <button
                    type="button"
                    style={{
                      cursor: "pointer",
                      fontSize: "11px",
                      color: theme.secondaryColour,
                      background: "none",
                      border: "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllImpacts(true);
                    }}
                  >
                    + {hiddenImpactsCount} more
                  </button>
                : null}
                {showAllImpacts && hiddenImpactsCount > 0 ?
                  <button
                    type="button"
                    style={{
                      cursor: "pointer",
                      fontSize: "11px",
                      color: theme.secondaryColour,
                      background: "none",
                      border: "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllImpacts(false);
                    }}
                  >
                    Hide
                  </button>
                : null}
              </div>
            </div>
          : null}
          {impactsToRender.length !== 0 ?
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "4px",
              }}
            >
              {impactsToRender.map((i) => (
                <Popup
                  key={i.impactId}
                  position="right center"
                  content={generateImpactText(t, usesImpactStatementSystem, i)}
                  trigger={
                    <Label
                      basic
                      color={
                        i.ownerImpact?.effect === "cost" ? "red"
                        : i.ownerImpact?.effect === "saving" ?
                          "green"
                        : "grey"
                      }
                      size="tiny"
                      style={{ width: "100%", margin: 0, overflow: "hidden" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            overflow: "hidden",
                          }}
                        >
                          <Icon name={i.ownerImpact?.icon} />
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {annualImpactValue(usesImpactStatementSystem, i)}
                          </div>
                        </div>
                        {i.choice?.weight !== undefined ?
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Icon
                              name="level up alternate"
                              style={{ marginLeft: 4, marginRight: 2 }}
                            />
                            {i.choice?.weight || 0}/
                            {i.ownerImpact?.maxWeight || 0}
                          </div>
                        : null}
                      </div>
                    </Label>
                  }
                />
              ))}
            </div>
          : null}
        </div>
      )}
      {idea.lockedOwnerIdeaBusinessProfile || idea.owner ?
        <MetaContainer>
          {idea.lockedOwnerIdeaBusinessProfile || idea.owner ?
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginRight: 5,
                overflow: "auto",
              }}
            >
              <span
                style={{ fontSize: 12, lineHeight: "16px", color: "#7a7a7a" }}
              >
                Created by{" "}
                {idea.lockedOwnerIdeaBusinessProfile ?
                  <IdeaBusinessProfileChip
                    compact="very"
                    ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                  />
                : <>
                    {loading ?
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Placeholder
                          style={{ height: 30, width: 30, borderRadius: 5 }}
                        >
                          <Placeholder.Image square />
                        </Placeholder>
                      </div>
                    : null}
                    {idea.owner ?
                      <UserChip user={idea.owner} compact="very" />
                    : null}
                  </>
                }
              </span>
            </div>
          : <div />}
        </MetaContainer>
      : null}
    </StyledCard>
  );
};

export default CustomCard;
